import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi, useToast } from '~/contexts';
import { useIsMounted } from '~/hooks';

/** Groups a member's associations into titled sections, shared by the list view and the clipboard export. */
export function buildSections(data) {
  return [
    { title: `${data.name} is a Project Admin on these projects`, data: data.projectAdmin },
    { title: `${data.name} is a Sales Representative on these clients`, data: data.clientSalesRepresentative },
    { title: `${data.name} is a Sales Representative on these projects`, data: data.projectSalesRepresentative },
    { title: `${data.name} is a named time approver on these projects`, data: data.timeApproval },
    { title: `${data.name} is a named expense approver on these projects`, data: data.expenseApproval },
    { title: `${data.name} is a Client Relationship Owner on these clients`, data: data.clientOwner },
    { title: `${data.name} is the manager of the following members`, data: data.managedMember },
  ];
}

function associationsToText(data) {
  let str = ``;
  buildSections(data).forEach(({ title, data: items }) => {
    if (items?.length > 0) {
      str += `\n${title}:\n`;
      items.forEach((item) => {
        switch (item.type) {
          case 'projectAdmin':
            str += ` - ${item.project.name} | ${item.project.client.name}\n`;
            break;
          case 'projectSalesRepresentative':
          case 'timeApproval':
          case 'expenseApproval':
            str += ` - ${item.name} | ${item.client.name}\n`;
            break;
          case 'client':
          case 'member':
            str += ` - ${item.name}\n`;
        }
      });
    }
  });

  return str;
}

/**
 * Fetches a member's associations and exposes whether any exist plus a clipboard
 * export. Shared by the deactivation confirmation and the associations modal.
 */
export default function useMemberAssociations({ memberId, workspaceId }) {
  const api = useApi();
  const toast = useToast();
  const isMounted = useIsMounted();
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState({
    name: '',
    projectAdmin: [],
    clientSalesRepresentative: [],
    projectSalesRepresentative: [],
    timeApproval: [],
    expenseApproval: [],
    clientOwner: [],
    managedMember: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const { data: associations } = await api.www.workspaces(workspaceId).members(memberId).getAssociations();
      if (!isMounted.current) return;
      setData(associations);
    } catch (err) {
      if (!isMounted.current) return;
      setError(true);
      toast.error(err.message);
    } finally {
      if (isMounted.current) setIsReady(true);
    }
  }, [memberId, workspaceId, api.www, isMounted, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isNotEmpty = useMemo(() => buildSections(data).some(({ data: items }) => items?.length > 0), [data]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(associationsToText(data));
    toast.success('Member associations copied to clipboard.');
  }, [data, toast]);

  return { data, isReady, isNotEmpty, error, copyToClipboard };
}
